import { DetailedStore, OpeningHours } from '../storefinder/interfaces';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { Link as LinkProps, LinkTarget } from '@spa/lib/react/hooks/useLinkAttributes';
import ButtonComponent from '../button/button/button.component';
import { ButtonType } from '../button/types/ButtonType';
import { ConditionalButton } from '../button/conditional-button/conditional-button';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import PickupInfoComponent from '@spa/components/storefinder/pickup-info/pickup-info.component';
import type { ProductInfoProduct } from '@spa/components/product-info/ProductInfoProductTypes';
import { StoreSearchProductInfoComponent } from '@spa/components/storefinder/store-search/store-search-product-info.component';
import StoreStockInfoComponent from '@spa/components/storefinder/store-stock-info/store-stock-info.component';
import { getStoreQueryLinkParams } from '@spa/lib/storeSlug';
import replaceDiagraphsWithDiacritics from '@spa/lib/replaceDiagraphsWithDiacritics';
import selectLocale from '@spa/lib/store/settings/selectors/selectLocale';
import styles from './map-card.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import { useSelector } from 'react-redux';
import { useUserLocation } from '@spa/lib/react/hooks/useUserLocation';

export interface MapCardComponentProps {
  readonly isProductAvailability: boolean;
  readonly isHomepage: boolean;
  readonly isSelectedStore: boolean;
  readonly product?: ProductInfoProduct | null;
  readonly store: DetailedStore;
  readonly storefinderSearchTerm?: string | null;
  readonly setShownStore: Dispatch<SetStateAction<DetailedStore | null>>;
  readonly setSelectedStore: (newSelectedStore: DetailedStore) => void;
}

function MapCardComponent({
  isProductAvailability,
  isHomepage,
  isSelectedStore,
  product,
  store,
  storefinderSearchTerm,
  setShownStore,
  setSelectedStore
}: MapCardComponentProps): ReactElement {
  const { location } = useUserLocation();
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('storefinder');
  const {
    address: { street, town, zip },
    code,
    company,
    geoLocation: { lat, lng },
    name,
    openingHours,
    phone,
    storePickUp
  } = store;

  const isStoreSelection: boolean = !isProductAvailability && !isHomepage && storePickUp;
  const locale = useSelector(selectLocale);

  const storeAssortments = [...new Set(store.assortments)]?.map((assortment) =>
    replaceDiagraphsWithDiacritics(assortment, locale)
  );

  const checkIcon = (
    <ImageComponent src={'/assets/icons/check.svg'} height={16} width={16} alt="check icon" />
  );

  const breadCrumbText =
    isHomepage || isSelectedStore ? formatMessage('showAllStores') : formatMessage('back');

  const setShowStoreWrapper = (): void => setShownStore(null);
  const setSelectedStoreWrapper = (): void => setSelectedStore(store);

  const storefinderLink: LinkProps = {
    type: 'internal',
    resolved: true,
    linkTitle: null,
    path: `/storefinder`,
    target: LinkTarget.SELF,
    // Return to the searched listing when the detail page was reached from a search.
    linkParams: getStoreQueryLinkParams(storefinderSearchTerm),
    anchorTarget: null
  };

  return (
    <div className={styles['map-card']}>
      <div className={styles['map-card__back-btn-wrapper']}>
        <ConditionalButton
          condition={!isHomepage}
          onButtonClick={setShowStoreWrapper}
          buttonType={ButtonType.AS_TEXT}
          text={breadCrumbText}
          link={storefinderLink}
        />
      </div>

      {product && (
        <div className={styles['map-card__product-availability']}>
          <StoreSearchProductInfoComponent product={product} />
          <StoreStockInfoComponent productCode={product.code} storeCode={code} />
        </div>
      )}

      <span className={'h3 bold'}>{name}</span>

      <div className={styles['map-card__store-info']}>
        <span>{formatMessage('storeNumber', { code })}</span>
        {company && <span>{company.name}</span>}
        <span>{street}</span>
        <span>
          {zip} {town}
        </span>
      </div>

      <span>{formatMessage('phone', { phone })}</span>

      {storeAssortments.length > 0 && (
        <div className={styles['map-card__store-assortments']}>
          <div className={`${styles['map-card__store-assortments-header']} bold`}>
            {formatMessage('productRange')}
          </div>

          {storeAssortments.map((storeAssortment) => {
            const translatedAssortment: string = formatMessage(
              `refineSearchOptions.${storeAssortment.toLowerCase()}`
            );

            return (
              <span
                className={styles['map-card__store-assortments-item']}
                key={translatedAssortment}
              >
                <span>{translatedAssortment}</span>
                {checkIcon}
              </span>
            );
          })}
        </div>
      )}

      {isStoreSelection && store.pickupTime && <PickupInfoComponent time={store.pickupTime} />}

      {openingHours.length > 0 && (
        <table className={styles['map-card__dates']}>
          <caption className={'bold'}>{formatMessage('openingHours')}</caption>
          <thead>
            <tr>
              <th className="sr-only">{formatMessage('dayOfWeek')}</th>
              <th className="sr-only">{formatMessage('openingHours')}</th>
            </tr>
          </thead>
          <tbody>
            {openingHours.map(
              ({ openingTime, closingTime, startPauseTime, endPauseTime, day }: OpeningHours) => (
                <tr key={day} className={styles['map-card__dates-timestamps']}>
                  <td className={styles['map-card__day']}>{formatMessage(`daysOfWeek.${day}`)}</td>
                  {startPauseTime && endPauseTime ? (
                    <td className={styles['map-card__time']}>
                      {openingTime} - {startPauseTime}
                      <br />
                      {endPauseTime} - {closingTime}
                    </td>
                  ) : (
                    <td className={styles['map-card__time']}>
                      {openingTime} - {closingTime}
                    </td>
                  )}
                </tr>
              )
            )}
          </tbody>
        </table>
      )}

      <div className={styles['map-card__actions']}>
        <Link
          prefetch={false}
          href={`https://www.google.com/maps/dir/${location?.lat}, ${location?.lng}/${lat},${lng}`}
          target={'_blank'}
          className={styles['map-card__action']}
        >
          <ButtonComponent buttonType={ButtonType.TERTIARY} text={formatMessage('route')} />
        </Link>

        {isSelectedStore ? (
          <ConditionalButton
            condition={!isHomepage}
            buttonType={ButtonType.SECONDARY}
            text={formatMessage('changeStore')}
            onButtonClick={setShowStoreWrapper}
            link={storefinderLink}
          />
        ) : (
          <ConditionalButton
            condition={!isHomepage}
            buttonType={ButtonType.SECONDARY}
            text={formatMessage('selectStore')}
            onButtonClick={setSelectedStoreWrapper}
            onLinkClick={setSelectedStoreWrapper}
            link={storefinderLink}
          />
        )}
      </div>
    </div>
  );
}

export default MapCardComponent;
